import { displayValue } from '@bloo-team/api-client'
import type { SearchRow } from '@bloo-team/api-client'

/**
 * Charts for aggregated search results (`groupby` queries): horizontal bar,
 * donut, treemap. Inline SVG/HTML, no library.
 *
 * Colour follows the dataviz method:
 * - The BAR chart encodes one measure across labelled categories — a single
 *   accent hue; identity lives in the labels, not in colour.
 * - The DONUT encodes identity — categorical slots `--viz-1..6` + a muted
 *   "Other", assigned in display order and never cycled. The palette is the
 *   validated reference set (both modes pass on this app's surfaces; the
 *   light mode's contrast WARN is relieved by direct labels and the table
 *   that always renders below the chart).
 * - The TREEMAP encodes magnitude (volume dominance) — a sequential ramp of
 *   the accent against the surface, light→dark with value. Identity is the
 *   in-box labels and tooltips.
 *
 * Every value mark is clickable and pivots exactly like a cell (the page
 * supplies the callback); "Other" is an aggregate, so it never pivots.
 */

export interface AggDatum {
  /** The category's raw value, as the result spelled it. */
  value: string
  count: number
}

/** Rows of a 2-column aggregated result -> sorted chart data. */
export function toAggData(rows: SearchRow[], valueField: string, countField: string): AggDatum[] {
  return rows
    .map((r) => ({ value: String(r[valueField] ?? '—'), count: Number(r[countField]) }))
    .filter((d) => Number.isFinite(d.count))
    .sort((a, b) => b.count - a.count)
}

/** Top N, the tail folded into one "Other" row (null value = not pivotable). */
function foldOther(data: AggDatum[], top: number): Array<AggDatum & { other?: boolean }> {
  if (data.length <= top) return data
  const head = data.slice(0, top)
  const rest = data.slice(top).reduce((s, d) => s + d.count, 0)
  return [...head, { value: `Other (${data.length - top})`, count: rest, other: true }]
}

const fmtN = (n: number) => n.toLocaleString('en-US')

/* ------------------------------------------------------------ horizontal bar */

export function AggBar({
  data,
  field,
  onPick,
}: {
  data: AggDatum[]
  field: string
  onPick?: (value: string) => void
}) {
  const shown = foldOther(data, 30)
  const max = shown.reduce((m, d) => Math.max(m, d.count), 1)
  return (
    <div className="agg-bars" role="img" aria-label={`Count by ${field}`}>
      {shown.map((d) => (
        <button
          key={d.value}
          type="button"
          className="agg-bar-row"
          disabled={d.other || !onPick}
          onClick={() => onPick?.(d.value)}
          title={`${displayValue(field, d.value)} — ${fmtN(d.count)}`}
        >
          <span className="agg-bar-label mono">{d.other ? d.value : displayValue(field, d.value)}</span>
          <span className="agg-bar-track">
            <span
              className="agg-bar-fill"
              data-other={d.other || undefined}
              style={{ width: `${Math.max(0.5, (d.count / max) * 100)}%` }}
            />
            <span className="agg-bar-n">{fmtN(d.count)}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------- donut */

export function AggDonut({
  data,
  field,
  onPick,
}: {
  data: AggDatum[]
  field: string
  onPick?: (value: string) => void
}) {
  const shown = foldOther(data, 6)
  const total = shown.reduce((s, d) => s + d.count, 0) || 1
  const R = 42
  const C = 2 * Math.PI * R
  // Precomputed, pure: each segment's start offset is the sum of the arcs
  // before it (n ≤ 7, the quadratic sum is nothing).
  const fracs = shown.map((d) => d.count / total)
  const offsets = fracs.map((_, i) => fracs.slice(0, i).reduce((s, f) => s + f * C, 0))
  return (
    <div className="agg-donut" role="img" aria-label={`Share of ${field}`}>
      <svg viewBox="0 0 120 120" className="agg-donut-svg">
        {shown.map((d, i) => {
          const frac = fracs[i]!
          // 2px angular gap between segments, in circumference units.
          const seg = Math.max(0, frac * C - 2)
          return (
            <circle
              key={d.value}
              cx="60"
              cy="60"
              r={R}
              fill="none"
              stroke={d.other ? 'var(--viz-other)' : `var(--viz-${i + 1})`}
              strokeWidth="14"
              strokeDasharray={`${seg} ${C - seg}`}
              strokeDashoffset={-offsets[i]! - 1}
              transform="rotate(-90 60 60)"
            >
              <title>{`${displayValue(field, d.value)} — ${fmtN(d.count)} (${Math.round(frac * 100)}%)`}</title>
            </circle>
          )
        })}
        <text x="60" y="57" textAnchor="middle" className="agg-donut-total">
          {fmtN(total)}
        </text>
        <text x="60" y="70" textAnchor="middle" className="agg-donut-sub">
          events
        </text>
      </svg>
      <ul className="agg-legend">
        {shown.map((d, i) => (
          <li key={d.value}>
            <button
              type="button"
              className="agg-legend-item"
              disabled={d.other || !onPick}
              onClick={() => onPick?.(d.value)}
            >
              <span
                className="agg-swatch"
                style={{ background: d.other ? 'var(--viz-other)' : `var(--viz-${i + 1})` }}
              />
              <span className="agg-legend-v mono">{d.other ? d.value : displayValue(field, d.value)}</span>
              <span className="agg-legend-n">
                {fmtN(d.count)} · {Math.round((d.count / total) * 100)}%
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ----------------------------------------------------------------- treemap */

interface TreeBox {
  d: AggDatum & { other?: boolean }
  x: number
  y: number
  w: number
  h: number
}

/**
 * Slice-and-dice with alternating direction — simpler than full squarify and
 * close enough at ≤13 boxes: each step slices the largest remaining item off
 * the shorter edge of the remaining rectangle.
 */
function layoutTreemap(data: Array<AggDatum & { other?: boolean }>, W: number, H: number): TreeBox[] {
  const total = data.reduce((s, d) => s + d.count, 0) || 1
  const boxes: TreeBox[] = []
  let x = 0
  let y = 0
  let w = W
  let h = H
  let rest = total
  for (const d of data) {
    const frac = rest > 0 ? d.count / rest : 0
    if (w >= h) {
      const bw = w * frac
      boxes.push({ d, x, y, w: bw, h })
      x += bw
      w -= bw
    } else {
      const bh = h * frac
      boxes.push({ d, x, y, w, h: bh })
      y += bh
      h -= bh
    }
    rest -= d.count
  }
  return boxes
}

export function AggTreemap({
  data,
  field,
  onPick,
}: {
  data: AggDatum[]
  field: string
  onPick?: (value: string) => void
}) {
  const shown = foldOther(data, 12)
  const W = 600
  const H = 220
  const max = shown.reduce((m, d) => Math.max(m, d.count), 1)
  const boxes = layoutTreemap(shown, W, H)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="agg-treemap" role="img" aria-label={`Volume by ${field}`}>
      {boxes.map(({ d, x, y, w, h }) => {
        // Sequential: accent mixed toward the surface, darker = more volume.
        const strength = d.other ? 12 : 22 + Math.round((d.count / max) * 66)
        return (
          <g key={d.value}>
            <rect
              x={x + 1}
              y={y + 1}
              width={Math.max(0, w - 2)}
              height={Math.max(0, h - 2)}
              rx={3}
              className="agg-treemap-box"
              style={{ fill: `color-mix(in srgb, var(--bui-accent) ${strength}%, var(--bui-surface))` }}
              onClick={d.other || !onPick ? undefined : () => onPick(d.value)}
              data-clickable={!d.other && !!onPick ? '' : undefined}
            >
              <title>{`${displayValue(field, d.value)} — ${fmtN(d.count)}`}</title>
            </rect>
            {w > 70 && h > 26 ? (
              <text x={x + 7} y={y + 16} className="agg-treemap-label">
                {(d.other ? d.value : displayValue(field, d.value)).slice(0, Math.floor(w / 7))}
              </text>
            ) : null}
            {w > 70 && h > 42 ? (
              <text x={x + 7} y={y + 30} className="agg-treemap-n">
                {fmtN(d.count)}
              </text>
            ) : null}
          </g>
        )
      })}
    </svg>
  )
}
